import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type Tab = { to: string; label: string; end?: boolean; badge?: number };

const groups: { label: string; tabs: Tab[] }[] = [
  {
    label: "Workflow",
    tabs: [
      { to: "/partner/research", label: "Home", end: true },
      { to: "/partner/research/items", label: "Items" },
      { to: "/partner/research/triage", label: "Triage" },
      { to: "/partner/research/suggestions", label: "Suggestions" },
      { to: "/partner/research/drafts", label: "Drafts" },
      { to: "/partner/research/capsules", label: "Capsules" },
      { to: "/partner/research/publication", label: "Publication" },
    ],
  },
  {
    label: "Inputs",
    tabs: [
      { to: "/partner/research/sources", label: "Sources" },
      { to: "/partner/research/ingest", label: "Ingest" },
      { to: "/partner/research/watch-rules", label: "Watch rules" },
      { to: "/partner/research/connectors", label: "Connectors" },
      { to: "/partner/research/fred", label: "FRED" },
    ],
  },
  {
    label: "Intelligence",
    tabs: [
      { to: "/partner/research/chat", label: "Chat" },
      { to: "/partner/research/kg", label: "Knowledge graph" },
      { to: "/partner/research/memory", label: "Memory" },
      { to: "/partner/research/predictions", label: "Predictions" },
      { to: "/partner/research/manager", label: "Manager" },
    ],
  },
  {
    label: "Reference & Filters",
    tabs: [
      { to: "/partner/research/taxonomy", label: "Taxonomy" },
      { to: "/partner/research/admin/url-blacklist", label: "URL blacklist" },
    ],
  },
  {
    label: "Quality & Ops",
    tabs: [
      { to: "/partner/research/evals", label: "Evals" },
      { to: "/partner/research/drift", label: "Drift" },
      { to: "/partner/research/prompts", label: "Prompts" },
      { to: "/partner/research/costs", label: "Costs" },
    ],
  },
];

export default function ResearchLayout() {
  // Nav-badge pending count for the Suggestions inbox. Self-gating SECURITY DEFINER
  // RPC (returns 0 for non-readers); polled so the badge stays roughly live.
  const { data: suggestionsPending } = useQuery({
    queryKey: ["research_suggestions_pending_count"],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)(
        "research_suggestions_pending_count",
      );
      if (error) throw error;
      return typeof data === "number" ? data : 0;
    },
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const decorate = (t: Tab): Tab =>
    t.to === "/partner/research/suggestions"
      ? { ...t, badge: suggestionsPending ?? 0 }
      : t;

  return (
    <div className="w-full px-4 py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-primary">Research Hub</h1>
        <p className="text-muted-foreground text-sm">
          Investment Team research, reviews and workflow.
        </p>
      </div>
      <nav className="space-y-2 border-b pb-2">
        {groups.map((g) => (
          <div key={g.label} className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 w-32 shrink-0">
              {g.label}
            </span>
            <div className="flex gap-1 flex-wrap">
              {g.tabs.map(decorate).map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end as any}
                  className={({ isActive }) =>
                    cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )
                  }
                >
                  {t.label}
                  {t.badge != null && t.badge > 0 && (
                    <span className="rounded-full bg-[hsl(var(--financial-gold)/0.18)] text-[hsl(38_60%_32%)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums leading-none">
                      {t.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
